import ClientLogger from '@eversports/react-components/utilities/client-logger'
import React, { ErrorInfo } from 'react'

import {
  BlockingErrorPage,
  IncorrectLoggedInUserErrorPage,
  NetworkErrorPage,
  UnexpectedErrorPage,
} from '../components/ErrorPages'
import { DefaultSimplifiedHeader } from '../components/Header'
import { CustomError } from '../lib/custom-errors'

interface State {
  hasUnexpectedError: boolean
  hasNetworkError: boolean
  hasBlockingError: boolean
  hasIncorrectLoggedInUserError: boolean
  blockingErrorMessage?: string
}

class ErrorBoundary extends React.Component<React.PropsWithChildren<unknown>> {
  public static getDerivedStateFromError(error: CustomError) {
    switch (error.type) {
      case 'NetworkError':
        return { hasNetworkError: true }
      case 'BlockingError':
        return { hasBlockingError: true, blockingErrorMessage: error.message }
      case 'IncorrectLoggedInUserError':
        return { hasIncorrectLoggedInUserError: true }
      default:
        return { hasUnexpectedError: true }
    }
  }

  public state: State = {
    hasUnexpectedError: false,
    hasNetworkError: false,
    hasBlockingError: false,
    hasIncorrectLoggedInUserError: false,
  }

  public componentDidCatch(error: CustomError, errorInfo: ErrorInfo) {
    switch (error.type) {
      case 'NetworkError':
        ClientLogger.debug(error.name, error, errorInfo)
        break
      case 'IncorrectLoggedInUserError':
        ClientLogger.debug(error.name, error, errorInfo)
        break
      case 'BlockingError':
        ClientLogger.debug(error.name, error, errorInfo)
        break
      default:
        ClientLogger.error(error.message, error, errorInfo)
    }
  }

  public render() {
    if (this.state.hasUnexpectedError) {
      return (
        <>
          <DefaultSimplifiedHeader />
          <UnexpectedErrorPage />
        </>
      )
    }

    if (this.state.hasBlockingError) {
      return (
        <>
          <DefaultSimplifiedHeader />
          <BlockingErrorPage message={this.state.blockingErrorMessage!} />
        </>
      )
    }

    if (this.state.hasIncorrectLoggedInUserError) {
      return (
        <>
          <DefaultSimplifiedHeader />
          <IncorrectLoggedInUserErrorPage />
        </>
      )
    }

    if (this.state.hasNetworkError) {
      return <NetworkErrorPage />
    }

    return this.props.children
  }
}

export default ErrorBoundary
